import {
  ProjectLanguage,
  SpecialVersionType,
  hexIdToHumanHexId,
} from "@hex/common";
import { useCallback } from "react";
import { useHistory } from "react-router";

import { ORG_ID } from "../../orgs";
import { HexRouteParams, Routes } from "../../route/routes";
import { useFeatureGates } from "../feature-gate/FeatureGateContext";
import { DEFAULT_PROJECT_TITLE } from "../logic/shared.js";
import { OnboardingDialogContext, OnboardingDialogId } from "../tour/dialogs";

import { useCreateHexMutation } from "./NewProjectDialog.generated";

type OnboardingOptions = {
  tourId?: string | undefined;
  onboardingDialogId?: OnboardingDialogId | undefined;
  onboardingDialogContext?: OnboardingDialogContext | undefined;
  projectName?: string | undefined;
};

export interface UseCreateNewDefaultProjectResult {
  createPythonProject: (
    onboarding?: OnboardingOptions,
    newTab?: boolean,
  ) => Promise<void>;
  createRProject: (
    onboarding?: OnboardingOptions,
    newTab?: boolean,
  ) => Promise<void>;
}

interface UseCreateNewDefaultProjectOptions {
  throwError?: boolean;
  routingMode?: "replace" | "push";
}

export function useCreateNewDefaultProject({
  routingMode = "push",
  throwError,
}: UseCreateNewDefaultProjectOptions = {}): UseCreateNewDefaultProjectResult {
  const { projectLimit } = useFeatureGates();

  const history = useHistory();
  const [createHex] = useCreateHexMutation();

  const createProject = useCallback(
    async (
      projectLanguage: ProjectLanguage,
      onboarding?: OnboardingOptions,
      newTab?: boolean,
    ) => {
      try {
        const { data } = await createHex({
          variables: {
            name: onboarding?.projectName ?? DEFAULT_PROJECT_TITLE,
            projectLanguage,
            description: "",
            statusId: null,
            categoryIds: [],
            kernelImage: null,
            kernelSize: null,
            getProjectCount: projectLimit != null,
            collectionId: null,
            collectionProjectRole: null,
            isTour:
              onboarding?.tourId != null ||
              onboarding?.onboardingDialogId != null,
          },
        });
        if (data?.createHex?.id == null) {
          throw new Error("Creation failed");
        }
        const logicViewArgs: Omit<HexRouteParams, "view"> = {
          hexLocator: hexIdToHumanHexId(
            data.createHex.id,
            data.createHex.title,
          ),
          version: SpecialVersionType.DRAFT,
          queryParams: onboarding?.onboardingDialogId
            ? {
                onboardingDialog: onboarding.onboardingDialogId,
                ...(onboarding.onboardingDialogContext
                  ? {
                      onboardingDialogContext:
                        onboarding.onboardingDialogContext,
                    }
                  : {}),
              }
            : onboarding?.tourId
              ? { tour: onboarding.tourId }
              : undefined,
        };
        if (newTab) {
          window.open(Routes.LOGIC.href(ORG_ID, true, logicViewArgs), "_blank");
        } else {
          Routes.LOGIC[routingMode](history, logicViewArgs);
        }
      } catch (e) {
        console.error(e);
        if (throwError) {
          throw e;
        }
      }
    },
    [createHex, projectLimit, history, routingMode, throwError],
  );

  const createPythonProject = useCallback(
    async (onboarding?: OnboardingOptions, newTab?: boolean) => {
      await createProject(ProjectLanguage.PYTHON, onboarding, newTab);
    },
    [createProject],
  );
  const createRProject = useCallback(
    async (onboarding?: OnboardingOptions, newTab?: boolean) => {
      await createProject(ProjectLanguage.R, onboarding, newTab);
    },
    [createProject],
  );

  return { createPythonProject, createRProject };
}
